import { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CheckCircleOutlined from '@mui/icons-material/CheckCircleOutlined';
import RemoveCircleOutlined from '@mui/icons-material/RemoveCircleOutlined';
import Chip, { ChipProps } from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import MonitorHeartOutlined from '@mui/icons-material/MonitorHeartOutlined';
import EventAvailableOutlined from '@mui/icons-material/EventAvailableOutlined';
import HealthAndSafetyOutlined from '@mui/icons-material/HealthAndSafetyOutlined';
import { Formatter } from '@omnigenbiodata/ui';
import { Participant } from '../../../../../../../../core/types/common.types';
import DetailRow from '../DetailRow';

interface ParticipantEHRProps {
  response: Participant;
}

interface EhrSectionProps {
  title: string;
  icon: ReactNode;
  children: ReactNode;
}

const formatDateTime = (value: string | null | undefined) =>
  value ? Formatter({ input: value, format: 'friendlyDateTime' }) : 'Not recorded';

function getEhrStatusColour(status: string | null | undefined): ChipProps['color'] {
  if (status === 'SUCCESS') {
    return 'success';
  }

  if (status === 'FAILED' || status === 'DECEASED') {
    return 'error';
  }

  if (!status || status === 'UNKNOWN') {
    return 'default';
  }

  return 'primary';
}

function getCardTone(status: string | null | undefined) {
  if (status === 'SUCCESS') {
    return {
      bgcolor: 'success.lighter',
      borderColor: 'success.light',
      iconColor: 'success' as const,
    };
  }

  if (status === 'FAILED' || status === 'DECEASED') {
    return {
      bgcolor: 'error.lighter',
      borderColor: 'error.light',
      iconColor: 'error' as const,
    };
  }

  if (!status || status === 'UNKNOWN') {
    return {
      bgcolor: 'background.paper',
      borderColor: 'divider',
      iconColor: 'primary' as const,
    };
  }

  return {
    bgcolor: 'info.lighter',
    borderColor: 'info.light',
    iconColor: 'info' as const,
  };
}

function renderEhrStatus(status: string | null | undefined) {
  const displayStatus = status || 'UNKNOWN';

  return <Chip label={displayStatus} color={getEhrStatusColour(displayStatus)} size="small" sx={{ fontWeight: 700 }} />;
}

function renderClinicalFlag(value: boolean | null | undefined, trueLabel: string, falseLabel: string) {
  if (value === null || value === undefined) {
    return 'Not recorded';
  }

  return (
    <Chip
      icon={value ? <CheckCircleOutlined /> : <RemoveCircleOutlined />}
      label={value ? trueLabel : falseLabel}
      color={value ? 'error' : 'success'}
      size="small"
      variant="outlined"
      sx={{
        fontWeight: 700,
        '& .MuiChip-icon': {
          fontSize: 18,
        },
      }}
    />
  );
}

function EhrSection({ title, icon, children }: EhrSectionProps) {
  return (
    <Box mt={2}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 1,
        }}
      >
        {icon}

        <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
      </Box>

      {children}
    </Box>
  );
}

function ParticipantEHR({ response }: ParticipantEHRProps) {
  const ehrStatus = response.ehr?.ehrStatus || 'UNKNOWN';
  const tone = getCardTone(ehrStatus);

  return (
    <Box mb={0}>
      <Paper
        elevation={4}
        sx={{
          bgcolor: tone.bgcolor,
          border: 1,
          borderColor: tone.borderColor,
          overflow: 'hidden',
        }}
      >
        <Box p={2}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              minWidth: 0,
              gap: 1,
              mb: 1,
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'background.paper',
                flexShrink: 0,
              }}
            >
              <MonitorHeartOutlined color={tone.iconColor} />
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" component="h2" sx={{ fontWeight: 700, m: 0 }}>
                Latest EHR Status
              </Typography>

              <Typography variant="body2" component="p" color="text.secondary">
                Current health record processing status and clinical flags
              </Typography>
            </Box>
          </Box>

          <DetailRow label="EHR Status" value={renderEhrStatus(ehrStatus)} />

          <Divider sx={{ my: 2 }} />

          <EhrSection title="EHR Dates" icon={<EventAvailableOutlined color={tone.iconColor} fontSize="small" />}>
            <DetailRow label="Data Arrived" value={formatDateTime(response.ehr?.dataHasArrivedTs)} />
            <DetailRow label="Last Updated" value={formatDateTime(response.ehr?.ehrUpdatedTs)} />
          </EhrSection>

          <Divider sx={{ my: 2 }} />

          <EhrSection title="Clinical Flags" icon={<HealthAndSafetyOutlined color={tone.iconColor} fontSize="small" />}>
            <DetailRow
              label="Loss of Capacity (LOC)"
              value={renderClinicalFlag(response.ehr?.ehrIsLoc, 'LOC recorded', 'No LOC recorded')}
            />
            <DetailRow
              label="Deceased"
              value={renderClinicalFlag(response.ehr?.ehrIsDeceased, 'Deceased', 'Not deceased')}
            />
          </EhrSection>
        </Box>
      </Paper>
    </Box>
  );
}

export default ParticipantEHR;
